// 🎯 일괄 처리 Dialog - 프리미엄 디자인
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Checkbox,
  Slide,
  LinearProgress,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Close as CloseIcon,
  PlaylistAddCheck as BulkIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  MonetizationOn as MoneyIcon,
  DevicesOther as DeviceIcon,
  Warning as WarningIcon,
  Send as SendIcon,
} from '@mui/icons-material';

import type { Application, DeviceInfo } from '../../../types/application';
import { DEVICE_STATUS_LIST } from '../../../../../constants/statusList';
import { getDeviceStatusColor } from '../../../../../constants/statusColors';
import { biztalkTemplates } from '../../../../../../verify-functions/src/utils/biztalkTemplates';
import { formatDate } from '../../orderDetail/utils';
import { sendAlimtalkPriceNotice } from '../../../../../utils/alerts';
import { AlertPreviewDialog } from './AlertPreviewDialog';
import { FormControlLabel } from '@mui/material';

// 🎯 애니메이션 전환
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// 📝 Props 인터페이스
interface BulkActionDialogProps {
  open: boolean;
  application: Application;
  devices: DeviceInfo[];
  onClose: () => void;
  onBulkUpdate: (deviceIds: string[], updates: Partial<DeviceInfo>) => Promise<void>;
  loading?: boolean;
}

// 🎯 액션 타입
type BulkActionType = 'approve' | 'reject' | 'status' | 'grade';

const BulkActionDialog: React.FC<BulkActionDialogProps> = ({
  open,
  application,
  devices,
  onClose,
  onBulkUpdate,
  loading = false
}) => {
  // 🔧 상태 관리
  const [actionType, setActionType] = useState<BulkActionType>('approve');
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    status: '',
    grade: '',
    price_offer: '',
    final_price: '',
    reject_reason: '',
  });
  const [processing, setProcessing] = useState(false);
  const [sendAlert, setSendAlert] = useState(true); // 기본값: 체크됨
  const [previewOpen, setPreviewOpen] = useState(false);
  const [perDevicePreviews, setPerDevicePreviews] = useState<Array<{
    deviceId: string;
    deviceModel: string;
    grade?: string | null;
    priceStr: string;
    event: string;
    message: string;
    errors: string[];
  }>>([]);

  // 🔧 디바이스 선택 토글
  const handleDeviceToggle = (deviceId: string) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  // 🔧 전체 선택/해제
  const handleSelectAll = () => {
    if (selectedDevices.length === devices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(devices.map(d => d.id));
    }
  };

  // 🔧 액션 타입별 처리
  const getActionConfig = (type: BulkActionType) => {
    const configs = {
      approve: {
        title: '검수 완료',
        icon: <CheckIcon />,
        color: '#4caf50',
        description: '선택된 기기들을 검수 완료 처리하고 가격을 안내합니다',
      },
      reject: {
        title: '일괄 반송',
        icon: <CancelIcon />,
        color: '#f44336',
        description: '선택된 기기들을 반송 처리합니다',
      },
      status: {
        title: '상태 변경',
        icon: <BulkIcon />,
        color: '#ff9800',
        description: '선택된 기기들의 상태를 일괄 변경합니다',
      },
      grade: {
        title: '등급 설정',
        icon: <DeviceIcon />,
        color: '#9c27b0',
        description: '선택된 기기들의 등급을 일괄 설정합니다',
      },
    };
    return configs[type];
  };

  // 💾 일괄 처리 실행
  const handleBulkAction = async () => {
    if (selectedDevices.length === 0) return;

    setProcessing(true);
    try {
      const updates: Partial<DeviceInfo> = {};

      switch (actionType) {
        case 'approve':
          // 🎯 일괄 승인 시 '가격안내' 상태로 변경 (검수가격만으로도 저장 가능)
          updates.status = '가격안내';
          if (formData.final_price) {
            updates.final_price = parseInt(formData.final_price, 10);
          }
          // 알림톡 전송 옵션: 미리보기 먼저 띄우기 (가격안내 per-device)
          if (sendAlert) {
            // 선택된 디바이스 목록만 대상으로 미리보기 생성
            const selectedSet = new Set(selectedDevices);
            const targetDevices = devices.filter(d => selectedSet.has(d.id));
            const items = targetDevices
              .filter(d => d.price_offer !== null && d.price_offer !== undefined)
              .map(d => {
                // 🆕 단일 '가격안내' 템플릿 사용
                const event = '가격안내';
                const t = (biztalkTemplates as any)[event];
                const priceStr = (d.price_offer || 0).toLocaleString();
                // 🆕 차감사유 라인: 값이 있을 때만 표시
                const deductionReasonLine = d.deduction_reason?.trim() 
                  ? `차감사유 : ${d.deduction_reason.trim()}` 
                  : '';
                const msg = t?.getMessage({
                  userName: application.user_name,
                  deviceName: d.deviceModel,
                  applyDate: formatDate(application.created_at),
                  price: priceStr,
                  application_id: application.id,
                  device_id: d.id,
                  // 🆕 가격안내 단일 템플릿용 파라미터
                  grade: d.grade || '-',
                  deductionReasonLine,
                  applicationNumber: application.application_number?.toString() || application.id,
                }) || '';
                const errors: string[] = [];
                if (!t) errors.push('템플릿 누락');
                if (t?.buttons) {
                  t.buttons.forEach((btn: any) => {
                    if (btn.type === 'WL') {
                      if (!btn.url_mobile || !btn.url_pc) errors.push(`버튼 URL 누락: ${btn.name}`);
                    }
                  });
                } else {
                  errors.push('버튼 정보 누락');
                }
                if (d.price_offer === null || d.price_offer === undefined) errors.push('검수금액 누락');
                return { deviceId: d.id, deviceModel: d.deviceModel, grade: d.grade, priceStr, event, message: msg, errors };
              });
            setPerDevicePreviews(items);
            // 미리보기 열고, 저장/전송은 확인에서 처리
            setPreviewOpen(true);
            setProcessing(false);
            return; // 여기서 종료(확인에서 계속)
          }
          break;
        case 'reject':
          updates.status = '반송';
          updates.reject_reason = formData.reject_reason;
          break;
        case 'status':
          updates.status = formData.status as DeviceInfo['status'];
          break;
        case 'grade':
          updates.grade = formData.grade as DeviceInfo['grade'];
          break;
      }

      if (formData.price_offer && actionType !== 'reject') {
        updates.price_offer = parseInt(formData.price_offer, 10);
      }

      await onBulkUpdate(selectedDevices, updates);
      onClose();
    } catch (error) {
      console.error('Bulk action error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const config = getActionConfig(actionType);
  const isValidAction = selectedDevices.length > 0 && 
    (actionType !== 'reject' || formData.reject_reason) &&
    (actionType !== 'status' || formData.status) &&
    (actionType !== 'grade' || formData.grade);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="lg"
      fullWidth
      fullScreen={window.innerWidth < 900} // 태블릿 이하에서 전체화면
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: 4 }, // 모바일/태블릿에서 둥근 모서리 제거
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
          minHeight: { xs: '100vh', md: '60vh' }, // 모바일에서 전체 높이
          maxHeight: { xs: '100vh', md: '90vh' },
        },
      }}
    >
      {/* 🎨 헤더 */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}CC 100%)`,
          color: 'white',
          p: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              width: 48,
              height: 48,
            }}
          >
            {config.icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              {config.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {config.description}
            </Typography>
          </Box>
          <Tooltip title="닫기">
            <IconButton
              onClick={onClose}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, // 모바일에서 세로 배치
            minHeight: { xs: 'auto', md: '500px' },
            height: { xs: 'calc(100vh - 200px)', md: 'auto' }, // 모바일에서 높이 조정
          }}
        >
          {/* 👈 액션 선택 섹션 */}
          <Box 
            sx={{ 
              width: { xs: '100%', md: 300 }, 
              borderRight: { xs: 'none', md: '1px solid #e1e8ed' },
              borderBottom: { xs: '1px solid #e1e8ed', md: 'none' }, // 모바일에서 하단 테두리
              p: { xs: 2, md: 3 },
            }}
          >
            <Typography variant="h6" fontWeight={700} gutterBottom>
              처리 유형 선택
            </Typography>
            
            <Stack spacing={2}>
              {(['approve', 'reject', 'status', 'grade'] as BulkActionType[]).map((type) => {
                const typeConfig = getActionConfig(type);
                return (
                  <Box
                    key={type}
                    onClick={() => setActionType(type)}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: actionType === type ? typeConfig.color : '#e1e8ed',
                      bgcolor: actionType === type ? `${typeConfig.color}08` : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: typeConfig.color,
                        bgcolor: `${typeConfig.color}04`,
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          bgcolor: typeConfig.color,
                          width: 32,
                          height: 32,
                        }}
                      >
                        {React.cloneElement(typeConfig.icon, { sx: { fontSize: 16 } })}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {typeConfig.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type === 'approve' && '가격 안내'}
                          {type === 'reject' && '반송 처리'}
                          {type === 'status' && '상태 변경'}
                          {type === 'grade' && '등급 지정'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>

            {/* 📊 선택 요약 */}
            <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                선택 요약
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedDevices.length}개 기기 선택됨
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(selectedDevices.length / devices.length) * 100}
                sx={{ mt: 1, height: 8, borderRadius: 4 }}
              />
            </Box>
          </Box>

          {/* 👉 오른쪽: 기기 목록 + 폼 */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* 📋 기기 목록 */}
            <Box sx={{ flex: 1, p: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                  처리할 기기 선택
                </Typography>
                <Button
                  onClick={handleSelectAll}
                  size="small"
                  sx={{ textTransform: 'none' }}
                >
                  {selectedDevices.length === devices.length ? '전체 해제' : '전체 선택'}
                </Button>
              </Stack>

              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {devices.map((device, index) => (
                  <ListItem
                    key={device.id}
                    sx={{
                      border: '1px solid #e1e8ed',
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: selectedDevices.includes(device.id) ? 'primary.50' : 'white',
                    }}
                  >
                    <Checkbox
                      checked={selectedDevices.includes(device.id)}
                      onChange={() => handleDeviceToggle(device.id)}
                      sx={{ mr: 2 }}
                    />
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {index + 1}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={device.deviceModel}
                      secondary={
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {device.deviceType}
                          </Typography>
                          <Chip
                            label={device.status}
                            size="small"
                            sx={{
                              bgcolor: getDeviceStatusColor(device.status),
                              color: 'white',
                              fontSize: '0.7rem',
                            }}
                          />
                        </Stack>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Divider />

            {/* 📝 폼 섹션 */}
            <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {config.title} 설정
              </Typography>

              <Stack spacing={3}>
                {/* 검수 완료 처리 */}
                {actionType === 'approve' && (
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="검수 가격 (선택)"
                      value={formData.price_offer}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_offer: e.target.value.replace(/[^0-9]/g, '') }))}
                      InputProps={{
                        startAdornment: <Typography color="text.secondary">₩</Typography>,
                      }}
                      sx={{ flex: 1 }}
                      placeholder="모든 기기에 동일 적용"
                    />
                    <TextField
                      label="최종 가격 (선택)"
                      value={formData.final_price}
                      onChange={(e) => setFormData(prev => ({ ...prev, final_price: e.target.value.replace(/[^0-9]/g, '') }))}
                      InputProps={{
                        startAdornment: <Typography color="text.secondary">₩</Typography>,
                      }}
                      sx={{ flex: 1 }}
                      placeholder="모든 기기에 동일 적용"
                    />
                  </Stack>
                )}

                {actionType === 'approve' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendAlert}
                        onChange={(e) => setSendAlert(e.target.checked)}
                        sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <SendIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="body2" fontWeight={600}>
                          고객에게 알림톡 전송 (가격안내)
                        </Typography>
                      </Stack>
                    }
                  />
                )}

                {/* 반송 처리 */}
                {actionType === 'reject' && (
                  <TextField
                    label="반송 사유"
                    value={formData.reject_reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, reject_reason: e.target.value }))}
                    multiline
                    rows={3}
                    required
                    placeholder="반송 사유를 상세히 입력하세요"
                  />
                )}

                {/* 상태 변경 */}
                {actionType === 'status' && (
                  <FormControl fullWidth>
                    <InputLabel>변경할 상태</InputLabel>
                    <Select
                      value={formData.status}
                      label="변경할 상태"
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    >
                      {DEVICE_STATUS_LIST.map((status) => (
                        <MenuItem key={status} value={status}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: getDeviceStatusColor(status),
                              }}
                            />
                            <Typography>{status}</Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* 등급 설정 */}
                {actionType === 'grade' && (
                  <FormControl fullWidth>
                    <InputLabel>등급</InputLabel>
                    <Select
                      value={formData.grade}
                      label="등급"
                      onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                    >
                      {['A', 'B', 'C', 'D'].map((grade) => (
                        <MenuItem key={grade} value={grade}>
                          {grade}등급
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* ⚠️ 알림 메시지 */}
                {actionType === 'approve' && selectedDevices.length > 0 && (
                  <Alert severity="info">
                    <Typography variant="body2" fontWeight={600}>
                      검수 완료 처리 시 {selectedDevices.length}개 기기가 '가격안내' 상태로 변경됩니다.
                    </Typography>
                  </Alert>
                )}
                
                {actionType === 'reject' && selectedDevices.length > 0 && (
                  <Alert severity="warning">
                    <Typography variant="body2" fontWeight={600}>
                      반송 처리 시 {selectedDevices.length}개 기기의 고객에게 알림톡이 자동 발송됩니다.
                    </Typography>
                  </Alert>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* 🎯 액션 버튼 - 모바일 최적화 */}
      <DialogActions 
        sx={{ 
          p: { xs: 2, md: 3 }, 
          pt: 0,
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' }, // 모바일에서 세로 배치
        }}
      >
        <Button
          onClick={onClose}
          disabled={processing}
          variant="outlined"
          fullWidth={window.innerWidth < 600} // 모바일에서 전체 너비
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            minHeight: { xs: 48, sm: 36 }, // 모바일에서 터치 친화적 높이
            fontSize: { xs: '1rem', sm: '0.875rem' },
            borderWidth: 2,
            '&:hover': { borderWidth: 2 },
            order: { xs: 2, sm: 1 }, // 모바일에서 순서 변경
          }}
        >
          취소
        </Button>
        <Button
          onClick={handleBulkAction}
          variant="contained"
          disabled={processing || !isValidAction || loading}
          startIcon={processing ? undefined : config.icon}
          fullWidth={window.innerWidth < 600} // 모바일에서 전체 너비
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 700,
            minHeight: { xs: 48, sm: 36 }, // 모바일에서 터치 친화적 높이
            fontSize: { xs: '1rem', sm: '0.875rem' },
            bgcolor: config.color,
            background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}CC 100%)`,
            boxShadow: `0 4px 20px ${config.color}40`,
            '&:hover': {
              background: `linear-gradient(135deg, ${config.color}DD 0%, ${config.color} 100%)`,
              boxShadow: `0 6px 28px ${config.color}60`,
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              background: 'grey.300',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            order: { xs: 1, sm: 2 }, // 모바일에서 순서 변경
          }}
        >
          {processing ? '처리 중...' : (
            <>
              {window.innerWidth < 600 ? config.title : `${config.title} (${selectedDevices.length}개)`}
            </>
          )}
        </Button>
      </DialogActions>

      {/* 👁️ 알림톡 미리보기 (가격안내 per-device) */}
      <AlertPreviewDialog
        open={previewOpen}
        application={application}
        alertPreviewMessage={perDevicePreviews.length === 0 ? '[가격안내] 전송할 항목이 없습니다.' : ''}
        perDevicePreviews={perDevicePreviews}
        isStatusSaving={processing}
        onClose={() => setPreviewOpen(false)}
        onConfirm={async () => {
          try {
            setProcessing(true);
            // 1) 상태 일괄 변경 저장
            const updates: Partial<DeviceInfo> = { status: '가격안내' };
            if (formData.final_price) {
              updates.final_price = parseInt(formData.final_price, 10);
            }
            if (formData.price_offer) {
              updates.price_offer = parseInt(formData.price_offer, 10);
            }
            await onBulkUpdate(selectedDevices, updates);
            // 2) 알림톡 per-device 발송 (선택된 기기만)
            await sendAlimtalkPriceNotice(application.id, false, selectedDevices);
            setPreviewOpen(false);
            onClose();
          } catch (e) {
            console.error('알림톡 전송 실패:', e);
          } finally {
            setProcessing(false);
          }
        }}
      />
    </Dialog>
  );
};

export default BulkActionDialog;